import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Box from '@mui/system/Box'
import { useRouterStore } from '../../RouterStore'
import { useMainContainerStore } from '../MainContainerStore'
import Stack from '@mui/material/Stack'
import KeyboardDoubleArrowLeftOutlined from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined'
import { themeConfig } from '@gap/shared/theme'
import SubHeader from 'reusable/mui/typography/SubHeader'
import GapIcon from '/images/The_Gap_Icon_White.svg'
import ListItemButton from '@mui/material/ListItemButton'
import Collapse from '@mui/material/Collapse'
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined'
import useStore from 'reusable/mobx-store-utils'
import { MenuChildItem } from '../MenuConfig'
import MenuStore from './MenuStore'
import { useLocation } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import { useThemeStore } from 'theme/ThemeStore'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import { useOrgGuardStore } from '../../OrgGuardStore'
import { completedVisibleStepCount, getVisibleGettingStartedSteps } from 'features/gap-components/getting-started/gettingStartedSteps'
import { routes } from '../RouteConfig'
import { apiClient } from 'api/trpc/trpc-client'
import { UserOnboardingDefaults } from 'variables/variables'

export default observer(function ExtendedMenu() {
   const routerStore = useRouterStore()
   const mainContainerStore = useMainContainerStore()
   const themeStore = useThemeStore()

   return <Stack
      sx={{
         width: '290px',
         height: '100%',
         flexDirection: 'column',
         backgroundColor: themeStore.dark ? 'grey' : themeConfig.primary[800],
         color: 'white',
      }}
      role="presentation"
   >
      <Box sx={{ flexShrink: 0, mt: 4, ml: 2, mr: 3 }}>
         <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <img src={GapIcon} width="46" height="32" alt="The Gap" style={{ cursor: 'pointer' }} onClick={mainContainerStore.navigateToOrgsDefault} />
            <Box sx={{ flex: 1 }} />
            <IconButton sx={{
               ml: 1,
               color: 'white',
               borderRadius: '8px',
               ':hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.16)',
               },
            }} onClick={mainContainerStore.menuState.close}>
               <KeyboardDoubleArrowLeftOutlined sx={{ mt: 0, cursor: 'pointer', ml: '-1px' }} />
            </IconButton>
         </Box>
      </Box>

      <Box sx={{
         flex: 1,
         minHeight: 0,
         overflowY: 'auto',
         pl: 2,
         pr: 2,
         mt: 4,
         '&::-webkit-scrollbar-thumb': {
            boxShadow: 'inset 0 0 0 10px rgba(255, 255, 255, 0.16)',
         },
      }}>
         {routerStore.menu.length >= 1 && routerStore.menu.map((menuParent, index) =>
            <Box key={`menuItem-${index}`} sx={{ mb: 4, ml: -2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
               {menuParent.title !== '' && <SubHeader sx={{ ml: 5 }}>{menuParent.title}</SubHeader>}
               {menuParent.children.map((child, index) => child.children?.length
                  ? <ExtendedMenuGroup child={child} key={`menuItemChild-${index}`} />
                  : <ExtendedMenuItem child={child} key={`menuItemChild-${index}`} />)}
            </Box>
         )}
      </Box>
      {mainContainerStore.sp.orgGuardStore.org?.orgType === 'advisor' && <GettingStartedCard />}
   </Stack>
})

const GettingStartedCard = observer(function GettingStartedCard() {
   const orgGuardStore = useOrgGuardStore()
   const routerStore = useRouterStore()

   const gettingStartedCardStore = useStore(sp => ({
      get canSeeGetStarted() {
         if (!sp.orgGuardStore.org) return false // Not loaded yet?
         return sp.orgGuardStore.featureToggles.meetings
      },
      get progress() {
         const visibleSteps = getVisibleGettingStartedSteps({
            hasMyOrganisationPermission: orgGuardStore.userHasMyOrganisationPermission,
            hasCalendarToggle: orgGuardStore.hasFeatureToggleAccess('calendar-integration'),
         })
         // A user with no onboarding record still counts the pre-completed setupAccount step.
         const completedSteps = completedVisibleStepCount({
            completedSteps: orgGuardStore.userData.onboarding?.gettingStartedSteps ?? UserOnboardingDefaults.gettingStartedSteps,
            visibleSteps,
         })
         return {
            progress: (completedSteps / visibleSteps.length) * 100,
            completedSteps: completedSteps,
            totalSteps: visibleSteps.length,
         }
      },
      onClickHandler: () => {
         // Expand getting started when navigating to meetings via this card
         sp.orgGuardStore.userData.onboarding = {
            ...sp.orgGuardStore.userData.onboarding,
            gettingStartedCollapsed: false,
         }
         apiClient.user.updateMyOnboardingProgress.mutate({ gettingStartedCollapsed: false })
         sp.routerStore.navigateToRoute(routes['meetings'])
      }
   }), { orgGuardStore, routerStore })

   if (!gettingStartedCardStore.canSeeGetStarted || orgGuardStore.userData.onboarding?.gettingStartedCompletedAt) {
      return null
   }

   return <Box
      sx={{
         flexShrink: 0,
         ml: 2,
         mr: 3,
         mt: 'auto',
         mb: 3,
         pt: 2,
         cursor: 'pointer',
      }}
      onClick={gettingStartedCardStore.onClickHandler}
   >
      <Card
         sx={{
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            color: 'white',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 'none',
            border: '1px solid rgba(255, 255, 255, 0.12)',
         }}
      >
         <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body1Bold" sx={{ color: 'inherit' }}>
               🚀 Getting started
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
               <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LinearProgress
                     variant="determinate"
                     value={gettingStartedCardStore.progress.progress}
                     sx={{
                        height: 6,
                        borderRadius: 0,
                        bgcolor: 'white',
                        '& .MuiLinearProgress-bar': {
                           borderRadius: 3,
                           bgcolor: themeConfig.system.success[500],
                        },
                     }}
                  />
               </Box>
               <Typography variant="caption" sx={{ flexShrink: 0, color: 'inherit', opacity: 0.85 }}>
                  {gettingStartedCardStore.progress.completedSteps} of {gettingStartedCardStore.progress.totalSteps}
               </Typography>
            </Box>
         </CardContent>
      </Card>
   </Box>
})

const ExtendedMenuItem = observer(function ExtendedMenuItem({ child }: { child: MenuChildItem }) {
   const mainContainerStore = useMainContainerStore()
   const routerStore = useRouterStore()
   const location = useLocation()

   const menuStore = useStore(sp => new MenuStore(sp), { child, path: location.pathname, routerStore, mainContainerStore })

   useEffect(() => {
      menuStore.updatePath(location.pathname)
      menuStore.setSxProps()
   }, [menuStore, location.pathname])

   return <ListItemButton sx={{ ...menuStore.sxProps, ml: 2, ':hover': { backgroundColor: themeConfig.dark.action.hover }, borderRadius: '8px' }} onClick={menuStore.handleClick}>
      <ListItemIcon sx={{ minWidth: '48px' }}><child.icon sx={{ color: 'white' }} /></ListItemIcon>
      <ListItemText primary={child.title} />
      {child.chip && <Chip label={child.chip} size="small" sx={{ backgroundColor: themeConfig.accents.orange[500], color: 'white' }} />}
   </ListItemButton>
})

// An expandable group: clicking the row toggles its sub-items (it does not navigate). Starts open if
// a sub-item happens to be the active page on mount, but the user is free to collapse it from there.
const ExtendedMenuGroup = observer(function ExtendedMenuGroup({ child }: { child: MenuChildItem }) {
   const mainContainerStore = useMainContainerStore()
   const routerStore = useRouterStore()
   const location = useLocation()

   const menuStore = useStore(sp => new MenuStore(sp), { child, path: location.pathname, routerStore, mainContainerStore })

   useEffect(() => {
      menuStore.updatePath(location.pathname)
      menuStore.setSxProps()
   }, [menuStore, location.pathname])

   return <>
      <ListItemButton sx={{ ...menuStore.sxProps, ml: 2, ':hover': { backgroundColor: themeConfig.dark.action.hover }, borderRadius: '8px' }} onClick={menuStore.toggleExpanded}>
         <ListItemIcon sx={{ minWidth: '48px' }}><child.icon sx={{ color: 'white' }} /></ListItemIcon>
         <ListItemText primary={child.title} />
         <ExpandMoreOutlined sx={{
            color: 'white',
            opacity: 0.85,
            transition: 'transform 0.2s',
            transform: menuStore.expanded ? 'rotate(180deg)' : 'rotate(0deg)'
         }} />
      </ListItemButton>
      <Collapse in={menuStore.expanded} timeout={200} unmountOnExit>
         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
         {child.children?.map(subItem =>
            <ListItemButton
               key={subItem.navPath}
               data-nav-path={subItem.navPath}
               onClick={menuStore.navigateToSubItem}
               sx={{
                  position: 'relative',
                  ml: 2,
                  pl: 9,
                  borderRadius: '8px',
                  ':hover': { backgroundColor: themeConfig.dark.action.hover },
                  ...(menuStore.isSubItemSelected(subItem.navPath) && {
                     '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.16)',
                        borderRadius: '8px',
                     },
                  }),
               }}
            >
               <ListItemText primary={subItem.title} />
            </ListItemButton>
         )}
         </Box>
      </Collapse>
   </>
})
